import React from 'react';
import { CheckCircle2, AlertCircle, Minus, HelpCircle } from 'lucide-react';

/**
 * HarvestCorrectionBadge — compact pill that surfaces the persisted
 * harvest-correction signal on a saved spreadsheet record. Strictly read-only.
 *
 * Status mapping:
 *   'verified'   → "Verified Harvest Correction"   (amber, CheckCircle2 icon)
 *   'possible'   → "Possible Harvest Correction"   (yellow, AlertCircle icon)
 *   'none'       → "No Harvest Correction"         (slate, Minus icon)
 *   null/undef   → "Not calculated"                (gray ghost, HelpCircle icon)
 *
 * Explanatory text per the PRD is shown on hover/focus via the native `title`
 * attribute — works on mouse hover, keyboard focus, and long-press on touch.
 */
const BADGE_CONFIG = {
  verified: {
    label: 'Verified Harvest Correction',
    short: 'Verified',
    tooltip:
      'Harvest-derived weights confirm that previous routine samples likely underestimated true average weight.',
    icon: CheckCircle2,
    classes:
      'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-700',
    testid: 'badge-harvest-correction-verified',
  },
  possible: {
    label: 'Possible Harvest Correction',
    short: 'Possible',
    tooltip:
      'A harvest-derived correction suggests possible historical sample underestimation, but confirmation is limited.',
    icon: AlertCircle,
    classes:
      'bg-yellow-100 text-yellow-900 border-yellow-300 dark:bg-yellow-900/25 dark:text-yellow-100 dark:border-yellow-700',
    testid: 'badge-harvest-correction-possible',
  },
  none: {
    label: 'No Harvest Correction',
    short: 'None',
    tooltip: 'No harvest-confirmed sample correction detected.',
    icon: Minus,
    classes:
      'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
    testid: 'badge-harvest-correction-none',
  },
  not_calculated: {
    label: 'Not calculated',
    short: 'Not calculated',
    tooltip:
      'This record predates the harvest-correction signal. Reanalyze or reupload to compute it.',
    icon: HelpCircle,
    classes:
      'bg-slate-50 text-slate-400 border-slate-200 border-dashed dark:bg-slate-900/40 dark:text-slate-500 dark:border-slate-700',
    testid: 'badge-harvest-correction-not-calculated',
  },
};

/**
 * Maps a persisted `harvest_correction_status` value to the badge key.
 * Strictly preserves the legacy distinction: null / undefined / "" / missing
 * → "not_calculated" so we never silently show "None" for a record that was
 * never analyzed under this signal.
 */
export function statusKey(status) {
  if (status === null || status === undefined || status === '') return 'not_calculated';
  const s = String(status).toLowerCase();
  if (s === 'verified') return 'verified';
  if (s === 'possible') return 'possible';
  if (s === 'none') return 'none';
  return 'not_calculated';
}

/**
 * Numeric rank used by the saved-records sort column.
 * Verified (0) → Possible (1) → None (2) → Not calculated (3).
 */
export function statusSortRank(status) {
  const key = statusKey(status);
  return { verified: 0, possible: 1, none: 2, not_calculated: 3 }[key];
}

/**
 * Human-readable label for filter dropdowns (exposed for filter accessors).
 */
export function statusFilterLabel(status) {
  return BADGE_CONFIG[statusKey(status)].short;
}

export default function HarvestCorrectionBadge({ status, compact = false }) {
  const key = statusKey(status);
  const cfg = BADGE_CONFIG[key];
  const Icon = cfg.icon;
  const text = compact ? cfg.short : cfg.label;

  return (
    <span
      role="status"
      tabIndex={0}
      aria-label={cfg.label}
      title={cfg.tooltip}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-medium leading-none whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 ${cfg.classes}`}
      data-testid={cfg.testid}
      data-tooltip={cfg.tooltip}
    >
      <Icon className="w-3 h-3" />
      <span>{text}</span>
    </span>
  );
}
